/**
 * CookielessFallbackPage — operator view for the cookieless fallback
 * fingerprint store. Three cards:
 * 1. Stats : tenant-scoped counters (total/active/expired + oldest seen).
 * 2. Lookup : hand-compute a fingerprint from (ip, ua, lang, screen) and
 * probe the store for a live hit.
 * 3. Sweep : DELETE /expired to reclaim expired rows.
 *
 * Brand: green headline; zinc borders; 6-8px rounding; plain
 * Tailwind only. All network calls go through ./api via ./hooks.
 */
import { useState, type FormEvent } from "react";
import {
	useCookielessFallbackLookup,
	useCookielessFallbackStats,
	useCookielessFallbackSweep,
} from "./hooks";
import type { CookielessFallbackLookupResult } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const LABEL = "flex flex-col gap-1 text-xs font-medium text-zinc-600";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

function formatTimestamp(value: number): string {
	if (!Number.isFinite(value) || value <= 0) return "-";
	const d = new Date(value * 1000);
	if (Number.isNaN(d.getTime())) return "-";
	return d.toISOString().replace("T","").slice(0, 19) + "Z";
}

function LookupResultBlock({
	result,
}: {
	result: CookielessFallbackLookupResult;
}) {
	if (result.hit) {
 return (
 <dl
 className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs"
 data-testid="cookieless-fallback-lookup-hit"
 >
 <dt className="text-zinc-500">fingerprint</dt>
 <dd className="truncate font-mono text-zinc-900">{result.fingerprint}</dd>
 <dt className="text-zinc-500">click_id</dt>
 <dd className="truncate font-mono text-zinc-900">{result.click_id || "-"}</dd>
 <dt className="text-zinc-500">campaign_id</dt>
 <dd className="truncate font-mono text-zinc-900">{result.campaign_id || "-"}</dd>
 <dt className="text-zinc-500">publisher_id</dt>
 <dd className="truncate font-mono text-zinc-900">{result.publisher_id || "-"}</dd>
 <dt className="text-zinc-500">hit_count</dt>
 <dd className="text-zinc-900">{result.hit_count}</dd>
 <dt className="text-zinc-500">ttl_seconds</dt>
 <dd className="text-zinc-900">{result.ttl_seconds}</dd>
 <dt className="text-zinc-500">expires_at</dt>
 <dd className="text-zinc-900">{formatTimestamp(result.expires_at)}</dd>
 </dl>
 );
	}
	return (
 <p
 className="mt-3 text-sm text-zinc-600"
 data-testid="cookieless-fallback-lookup-miss"
 >
 No live row for fingerprint{""}
 <span className="font-mono text-xs text-zinc-900">{result.fingerprint}</span>
 {result.reason ? (
 <span className="text-zinc-500"> (reason: {result.reason})</span>
 ) : null}
 .
 </p>
	);
}

export function CookielessFallbackPage() {
	const statsQuery = useCookielessFallbackStats();
	const lookupMut = useCookielessFallbackLookup();
	const sweepMut = useCookielessFallbackSweep();

	const [ip, setIp] = useState("");
	const [ua, setUa] = useState("");
	const [lang, setLang] = useState("");
	const [screen, setScreen] = useState("");

	const canLookup =
 (ip.trim() !== "" ||
 ua.trim() !== "" ||
 lang.trim() !== "" ||
 screen.trim() !== "") &&
 !lookupMut.isPending;

	const handleLookup = (e: FormEvent) => {
 e.preventDefault();
 if (!canLookup) return;
 lookupMut.mutate({
 ip: ip.trim(),
 user_agent: ua.trim(),
 lang: lang.trim(),
 screen: screen.trim(),
 });
	};

	const stats = statsQuery.data;

	return (
 <main
 data-testid="cookieless-fallback-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 cookieless-fallback
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Cookieless fallback
 </h1>
 <p className="max-w-3xl text-sm text-zinc-600">
 Short-lived, tenant-salted fingerprints for surfaces that cannot set a
 cookie. Inspect the current store, probe a signal tuple, or sweep
 expired rows.
 </p>
 </header>

 <section className={CARD} aria-label="Cookieless fallback stats">
 <div className="flex items-center justify-between gap-4">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Stats</h2>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => statsQuery.refetch()}
 disabled={statsQuery.isFetching}
 data-testid="cookieless-fallback-refresh"
 >
 {statsQuery.isFetching ? "Refreshing." : "Refresh"}
 </button>
 </div>
 {statsQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cookieless-fallback-loading"
 >
 Loading.
 </p>
 ) : statsQuery.error ? (
 <p
 className="mt-3 text-sm text-red-600"
 role="alert"
 data-testid="cookieless-fallback-stats-error"
 >
 {statsQuery.error.message}
 </p>
 ) : stats ? (
 <dl
 className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4"
 data-testid="cookieless-fallback-stats"
 >
 <div className="rounded-[6px] border border-zinc-200 p-3">
 <dt className="text-xs uppercase tracking-wide text-zinc-500">
 Total
 </dt>
 <dd className="mt-1 text-2xl font-semibold text-zinc-900">
 {stats.total}
 </dd>
 </div>
 <div className="rounded-[6px] border border-zinc-200 p-3">
 <dt className="text-xs uppercase tracking-wide text-zinc-500">
 Active
 </dt>
 <dd className="mt-1 text-2xl font-semibold text-green-600">
 {stats.active}
 </dd>
 </div>
 <div className="rounded-[6px] border border-zinc-200 p-3">
 <dt className="text-xs uppercase tracking-wide text-zinc-500">
 Expired
 </dt>
 <dd className="mt-1 text-2xl font-semibold text-zinc-500">
 {stats.expired}
 </dd>
 </div>
 <div className="rounded-[6px] border border-zinc-200 p-3">
 <dt className="text-xs uppercase tracking-wide text-zinc-500">
 Oldest seen
 </dt>
 <dd className="mt-1 text-sm font-medium text-zinc-700">
 {formatTimestamp(stats.oldest_seen_at)}
 </dd>
 </div>
 <div className="col-span-2 text-xs text-zinc-500 sm:col-span-4">
 TTL window: {stats.min_ttl_seconds}s &#x2013; {stats.max_ttl_seconds}s
 (default {stats.default_ttl_seconds}s). As of{""}
 {formatTimestamp(stats.as_of)}.
 </div>
 </dl>
 ) : (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cookieless-fallback-empty"
 >
 No stats available.
 </p>
 )}
 </section>

 <section className={CARD} aria-label="Cookieless fallback lookup">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Lookup</h2>
 <p className="mt-1 text-xs text-zinc-500">
 Probe the store with an (ip, user_agent, lang, screen) tuple. The
 server salts and hashes server-side; raw values never land in rows.
 </p>
 <form
 onSubmit={handleLookup}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
 data-testid="cookieless-fallback-lookup-form"
 >
 <label className={LABEL}>
 IP
 <input
 type="text"
 className={INPUT}
 placeholder="203.0.113.45"
 value={ip}
 onChange={(e) => setIp(e.currentTarget.value)}
 data-testid="cookieless-fallback-ip"
 />
 </label>
 <label className={LABEL}>
 User-Agent
 <input
 type="text"
 className={INPUT}
 placeholder="Mozilla/5.0."
 value={ua}
 onChange={(e) => setUa(e.currentTarget.value)}
 data-testid="cookieless-fallback-ua"
 />
 </label>
 <label className={LABEL}>
 Accept-Language
 <input
 type="text"
 className={INPUT}
 placeholder="en-US,en;q=0.9"
 value={lang}
 onChange={(e) => setLang(e.currentTarget.value)}
 data-testid="cookieless-fallback-lang"
 />
 </label>
 <label className={LABEL}>
 Screen (WxH or WxHxDPR)
 <input
 type="text"
 className={INPUT}
 placeholder="1920x1080x2"
 value={screen}
 onChange={(e) => setScreen(e.currentTarget.value)}
 data-testid="cookieless-fallback-screen"
 />
 </label>
 <div className="sm:col-span-2">
 <button
 type="submit"
 className={BTN}
 disabled={!canLookup}
 data-testid="cookieless-fallback-lookup-submit"
 >
 {lookupMut.isPending ? "Looking up." : "Look up"}
 </button>
 </div>
 </form>
 {lookupMut.error ? (
 <p
 className="mt-2 text-xs text-red-600"
 role="alert"
 data-testid="cookieless-fallback-lookup-error"
 >
 {lookupMut.error.message}
 </p>
 ) : null}
 {lookupMut.data ? <LookupResultBlock result={lookupMut.data} /> : null}
 </section>

 <section className={CARD} aria-label="Cookieless fallback sweep">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Sweep expired</h2>
 <p className="mt-1 text-xs text-zinc-500">
 Manually delete rows whose TTL has elapsed. Normally handled by the GC
 cron; use here for ad-hoc cleanup.
 </p>
 <div className="mt-3 flex items-center gap-3">
 <button
 type="button"
 className={BTN}
 onClick={() => sweepMut.mutate()}
 disabled={sweepMut.isPending}
 data-testid="cookieless-fallback-sweep"
 >
 {sweepMut.isPending ? "Sweeping." : "Sweep now"}
 </button>
 {sweepMut.data ? (
 <span
 className="text-sm text-zinc-700"
 data-testid="cookieless-fallback-sweep-result"
 >
 Deleted {sweepMut.data.deleted} row
 {sweepMut.data.deleted === 1 ? "" : "s"} (as of{""}
 {formatTimestamp(sweepMut.data.as_of)}).
 </span>
 ) : null}
 </div>
 {sweepMut.error ? (
 <p
 className="mt-2 text-xs text-red-600"
 role="alert"
 data-testid="cookieless-fallback-sweep-error"
 >
 {sweepMut.error.message}
 </p>
 ) : null}
 </section>
 </main>
	);
}

export default CookielessFallbackPage;
